import React from 'react';
import { Box, Stack, Typography, Chip, LinearProgress, CircularProgress } from '@mui/material';
import { OrderingPhase, TrioSelectionState } from '../types/voiceOrderingTypes';
import AgentSpeakerBar from './AgentSpeakerBar';

export interface VoiceStatusDisplayProps {
  // Voice states
  isOrderActive: boolean;
  isProcessingOrder: boolean;
  isConfirmingOrder: boolean;

  // Enhanced states
  phase: OrderingPhase;
  trioSelection: TrioSelectionState | null;

  // Transcripts
  currentTranscript: string;
  accumulatedTranscript: string;

  // Loading animation
  loadingDots: number;

  // Instructions
  instructions: string;

  // Agent
  agentName?: string;
  agentIsSpeaking?: boolean;
}

export default function VoiceStatusDisplay({
  isOrderActive,
  isProcessingOrder,
  isConfirmingOrder,
  phase,
  trioSelection,
  currentTranscript,
  accumulatedTranscript,
  loadingDots,
  instructions,
  agentName = '',
  agentIsSpeaking = false,
}: VoiceStatusDisplayProps) {

  const showGlobalLoading = isProcessingOrder || isConfirmingOrder;
  const showTrioLoading = phase === 'trio_selection' && !!trioSelection?.isLoading;

  const getOrderStatusInfo = () => {
    if (showGlobalLoading) return {
      icon: '🟡',
      title: 'Traitement en cours',
      instruction: 'Traitement en cours...'
    };

    switch (phase) {
      case 'idle':
        return {
          icon: '🔵',
          title: 'Prêt',
          instruction: 'Dites "Commencer la commande" pour débuter'
        };
      case 'ordering':
        return {
          icon: '🟢',
          title: 'Commande en cours',
          instruction: 'Parlez pour commander. Dites \'merci\' pour terminer, \'oublier\' pour réinitialiser, \'option trio\' pour un menu trio, ou \'supprimer + nom produit\' pour retirer un article.'
        };
      case 'trio_detected':
      case 'trio_selection':
        return {
          icon: '🍽️',
          title: 'Configuration Trio',
          instruction: 'Choisissez vos options ou dites "Annuler trio"'
        };
      case 'processing':
        return {
          icon: '⚙️',
          title: 'Traitement',
          instruction: 'Traitement en cours...'
        };
      case 'confirming':
        return {
          icon: '🟣',
          title: 'Commande confirmée',
          instruction: 'Veuillez passer à l\'autre fenêtre !'
        };
      case 'agent_requested':
        return {
          icon: '🙋',
          title: 'Agent en route',
          instruction: 'Un agent humain va vous prendre en charge dans quelques instants. Veuillez patienter.'
        };
      case 'agent_connected':
        return {
          icon: '✅',
          title: 'Agent connecté',
          instruction: "L'agent vous prend en charge. Vous pouvez vous exprimer librement."
        };
      case 'ai_resumed':
        return {
          icon: '🤖',
          title: 'Reprise avec l\'IA',
          instruction: "Aucun agent disponible. Je reprends la commande avec vous !"
        };
      default:
        return {
          icon: '🔴',
          title: 'En attente',
          instruction: 'En attente d\'une commande valide...'
        };
    }
  };

  const statusInfo = getOrderStatusInfo();

  const getPhaseEmoji = () => {
    switch (phase) {
      case 'trio_selection':
        return '🍽️';
      case 'trio_detected':
        return '🎯';
      case 'ordering':
        return '🎤';
      case 'processing':
        return '⚙️';
      case 'confirming':
        return '✅';
      default:
        return '🔴';
    }
  };

  const quickCommands: string[] = (() => {
    switch (phase) {
      case 'idle':
        return ['"commencer la commande"'];
      case 'ordering':
        return ['"merci"', '"oublier"', '"option trio"', '"supprimer + nom produit"', '"confirmer la commande"'];
      case 'trio_selection':
        return ['"suivant"', '"précédent"', '"répéter"', '"annuler trio"', '"valider ma demande trio"'];
      case 'confirming':
        return ['Confirmation en cours'];
      default:
        return [];
    }
  })();

  /* ── Agent connected : bannière dédiée ───────────────────────────────── */
  if (phase === 'agent_connected') {
    return (
      <>
        <Box sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(16,185,129,0.18)',
          border: '2px solid #10b981',
          mb: 1,
        }}>
          {/* Animated top bar */}
          <Box sx={{
            height: 5,
            background: 'linear-gradient(90deg, #10b981, #3b82f6, #10b981)',
            backgroundSize: '200% 100%',
            animation: 'agentBarMove 2s linear infinite',
            '@keyframes agentBarMove': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } },
          }} />

          {/* Main card */}
          <Box sx={{ p: 2.5, bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Avatar */}
            <Box sx={{
              width: 62,
              height: 62,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: agentIsSpeaking ? '0 0 0 4px rgba(16,185,129,0.3)' : 'none',
              transition: 'box-shadow 0.3s ease',
              animation: agentIsSpeaking ? 'avatarPulse 1s ease-in-out infinite' : 'none',
              '@keyframes avatarPulse': {
                '0%,100%': { boxShadow: '0 0 0 0 rgba(16,185,129,0.4)' },
                '50%': { boxShadow: '0 0 0 8px rgba(16,185,129,0)' },
              },
            }}>
              <Typography sx={{ fontSize: '28px' }}>🎧</Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#065f46', lineHeight: 1.2 }}>
                Un agent vous assiste
              </Typography>
              {agentName && (
                <Typography variant="body1" sx={{ color: '#059669', fontWeight: 600, mt: 0.25 }}>
                  {agentName} · Agent
                </Typography>
              )}
            </Box>

            {/* Speaking chip */}
            <Box sx={{ flexShrink: 0 }}>
              {agentIsSpeaking ? (
                <Chip label="Parle..." size="small"
                  sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#059669', fontWeight: 700, fontSize: '0.8rem', height: 28,
                    animation: 'speakBounce 0.8s ease-in-out infinite',
                    '@keyframes speakBounce': { '0%,100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' } },
                  }} />
              ) : (
                <Chip label="En ligne" size="small"
                  sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 600, fontSize: '0.8rem', height: 28 }} />
              )}
            </Box>
          </Box>

          {/* Instruction */}
          <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#dcfce7', borderTop: '1px solid #bbf7d0' }}>
            <Typography variant="body1" sx={{ color: '#166534', fontWeight: 500, textAlign: 'center' }}>
              Parlez librement — l&apos;agent vous entend et peut modifier votre commande
            </Typography>
          </Box>
        </Box>

        <AgentSpeakerBar agentName={agentName || 'Agent'} isSpeaking={agentIsSpeaking} />
      </>
    );
  }

  return (
    <>
    <Box>
      <Stack spacing={0}>
        {/* Main Status Header - White Box with Shadow */}
        <Box sx={{
          p: 2,
          bgcolor: 'white',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          {/* Microphone Icon Container */}
          <Box sx={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            bgcolor: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Typography sx={{ color: 'white', fontSize: '28px' }}>🎤</Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: '#333' }}>
              AI-LYN <Typography component="span" variant="h5" sx={{ fontWeight: 400 }}>{statusInfo.title.toLowerCase().includes('prêt') ? 'est prête' : 'écoute votre commande'}</Typography>
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#666', fontSize: '1.1rem' }}>
                {statusInfo.icon} {statusInfo.title}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Instructions Bar - Light Blue */}
        <Box sx={{
          p: 2,
          bgcolor: '#f2f7ff',
          borderRadius: 2,
          textAlign: 'center',
          mb: isOrderActive ? 1 : 3
        }}>
          <Typography variant="h6" sx={{ color: '#444', fontWeight: 500 }}>
            {statusInfo.instruction}
          </Typography>
        </Box>

        {/* Real-time transcripts - Commented out but preserved for logic/debugging if needed */}
        {/* 
        <Typography variant="body1">
          <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Parole actuelle:
          </Box>{' '}
          {currentTranscript}
        </Typography>

        {accumulatedTranscript && (
          <Typography variant="body1">
            <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Accumulé:
            </Box>{' '}
            {accumulatedTranscript}
          </Typography>
        )}
        */}

        {/* Trio Selection Info - Kept as requested for important DB info */}
        {phase === 'trio_selection' && trioSelection && (
          <Box sx={{
            p: 2,
            bgcolor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ff9800',
            mb: 2
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 1 }}>
              🍽️ Configuration Trio: {trioSelection.trioDetails.product.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Étape {trioSelection.currentGroupIndex + 1} sur {trioSelection.trioDetails.option_groups.length}
              {trioSelection.trioDetails.option_groups[trioSelection.currentGroupIndex] &&
                ` - ${trioSelection.trioDetails.option_groups[trioSelection.currentGroupIndex].name}`
              }
            </Typography>
            {showTrioLoading && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={18} thickness={5} />
                <Typography variant="caption" color="text.secondary">Traitement du choix...</Typography>
              </Stack>
            )}
          </Box>
        )}

        {/* Global Loading - Kept for visibility during processing */}
        {showGlobalLoading && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <LinearProgress sx={{ height: 4, borderRadius: 1 }} />
          </Box>
        )}

        {/* Quick Commands - Commented out for now to keep it as simple as the image */}
        {/* 
        {quickCommands.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {quickCommands.map((cmd) => (
              <Chip key={cmd} label={cmd} size="small" color="primary" variant="outlined" />
            ))}
          </Stack>
        )}
        */}
      </Stack>
    </Box>

  </>
  );
}
